import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { Typography } from '@otp/ui';
import { User as UserIcon, Logout, UserAvatar } from '@otp/icons';

import { User } from 'src/graphql/generated/graphql';
import { getFullName, getUserLevels } from 'src/utils/user-data';

import styles from './index.module.css';
import { useAuthContext } from 'src/context/auth.context';
import { Routes } from '../router/constants';
import { StopImpersonatingMenuItem } from './stop-impersonating-menu-item';
import { Link } from '../link';

interface UserProfileMenuProps {
  user?: User;
  logout: () => void;
}

export const UserProfileMenu = ({ user, logout }: UserProfileMenuProps) => {
  const { t } = useTranslation();
  const { isAdmin, isImpersonating } = useAuthContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  if (!user) {
    return <div />;
  }

  const open = Boolean(anchorEl);

  const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onLogoutClick = (event: React.MouseEvent<HTMLElement>) => {
    handleIconClick(event);
    logout();
  };

  const fullName = getFullName(user);

  return (
    <>
      <div className={styles.wrapper} onClick={handleIconClick}>
        <div className={styles.avatar}>
          <UserAvatar />
        </div>
        <div className={styles.info}>
          <Typography variant="subtitle2">
            <b>{fullName}</b>
          </Typography>
          <Typography variant="subtitle2">
            <b>
              {isAdmin
                ? t('common.admin')
                : getUserLevels(user).map((level) => (
                    <span>{t(`common.otp-levels.${level}`)}</span>
                  ))}
            </b>
          </Typography>
          <Typography variant="subtitle2">{user.email}</Typography>
        </div>
      </div>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        slotProps={{
          paper: {
            className: styles.menuWrapper,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        disableAutoFocusItem
      >
        <MenuItem className={styles.menuItem}>
          <Link to={Routes.profile.path}>
            <UserIcon />
            {t('user-profile-menu.settings-profile', 'Settings & Profile')}
          </Link>
        </MenuItem>
        {!isImpersonating && (
          <MenuItem onClick={onLogoutClick} className={styles.menuItem}>
            <Logout /> {t('common.log-out')}
          </MenuItem>
        )}
        {isImpersonating && <StopImpersonatingMenuItem />}
      </Menu>
    </>
  );
};

export default UserProfileMenu;
